// TIP-609 — the Counterparty Briefs endpoints.
//
// The contract document drafted these three as
//   GET /api/company/<companyID>/counterparty-briefs/allowance
//   GET /api/company/<companyID>/counterparty-briefs
//   GET /api/company/<companyID>/counterparty-briefs/<reference>
// The backend shipped them as
//   GET /api/briefs/allowance/?company_id=<companyID>
//   GET /api/briefs/orders/?company_id=<companyID>
//   GET /api/briefs/orders/<reference>/
//   POST /api/briefs/orders/
// with the company as a query parameter on the reads and in the body on the
// write. The shipped paths are the ones
// honoured here. The allowance body is a superset of the drafted one — it adds
// `state`, `can_order`, `next_brief_source`, `block_reason`, ISO reset
// timestamps alongside the display labels, and quantity bounds under pricing.
//
// One further departure matters to the caller: an unclaimed company answers
// **404**, not a 200 carrying claim_status: "unclaimed". mapAllowanceResponse()
// and useCounterpartyBriefs() between them turn that 404 back into the
// unclaimed claim gate, so nothing above this layer sees an HTTP status.
import { apiSlice } from "../apiSlice";
import { mapBriefsList } from "../../components/admin/Dashboard/counterpartyBriefs/briefsApiMapping";

// The page size the backend serves, and what the table's paging assumes.
export const BRIEFS_PAGE_SIZE = 10;

export const briefsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The allowance belongs to the business, not the user: companyID is the
    // company currently selected in the dashboard switcher.
    getBriefsAllowance: builder.query({
      query: ({ companyID, token }) => ({
        url: `api/briefs/allowance/?company_id=${encodeURIComponent(companyID)}`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      // The envelope carries statusCode alongside data; only data is of any
      // interest to the page.
      transformResponse: (response) => response?.data ?? null,
      // Ordering a brief spends allowance, so the card must not be allowed to
      // show a stale count after TIP-611 invalidates this tag.
      providesTags: (result, error, { companyID }) => [
        { type: "BriefsAllowance", id: companyID },
      ],
    }),

    // TIP-610 — every brief this business has ordered, one PAGE at a time.
    //
    // This used to walk every page into a single cache entry and let the table
    // search, filter and page over the rows in hand. That was forced by the
    // shipped response: it carried no `status_counts` and accepted no `status`
    // parameter, so counts computed from one page would have been wrong.
    //
    // The backend now sends `status_counts` (over the WHOLE unfiltered set, so
    // choosing a status does not rewrite the other numbers) and accepts
    // `search` and `status`. So this is a plain paged query again, and the
    // page, the search box and the filter are all server-driven.
    //
    // `status` is the customer-facing label — "Building your brief", not
    // `in_progress`. Two internal statuses collapse into that one label and
    // the client has never been given the internal vocabulary; the backend
    // rejects an internal value with a 400 rather than quietly matching it.
    //
    // Every parameter is part of the cache key, so each (page, search, status)
    // is its own entry and going back to a page already seen is instant.
    getBriefsList: builder.query({
      query: ({ companyID, token, page = 1, search = "", status = "" }) => {
        const params = new URLSearchParams({ company_id: companyID, page: String(page) });
        // Omitted rather than sent empty: `search=` and `status=` are both
        // falsy to the backend, but an absent parameter is the honest request.
        if (search) params.set("search", search);
        if (status) params.set("status", status);

        return {
          url: `api/briefs/orders/?${params.toString()}`,
          method: "GET",
          headers: {
            "content-type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };
      },
      transformResponse: (response) => mapBriefsList(response),
      providesTags: (result, error, { companyID }) => [{ type: "BriefsList", id: companyID }],
    }),

    // TIP-610 — one brief, by the reference the list row carries.
    getBriefDetail: builder.query({
      query: ({ reference, token }) => ({
        url: `api/briefs/orders/${encodeURIComponent(reference)}/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { reference }) => [{ type: "BriefDetail", id: reference }],
    }),

    // TIP-611 — the reference contact, looked up by email.
    //
    //   GET /api/briefs/requester-lookup/?email=<email>
    //
    // The email is the WHOLE query: no company_id. The directory is not scoped
    // to the business asking, so the lookup works before a company is selected
    // and the answer for an address is the same whoever asks for it.
    //
    // This replaces the DIRECTORY fixture the reference panel used to match
    // against, which held exactly one hardcoded address. The panel asks only
    // once the input holds a syntactically valid email — a partial address can
    // only ever miss, and typing a long one would otherwise fire a request per
    // keystroke.
    //
    // A 404 is the ANSWER "we do not know this person", not a failure: the
    // panel falls back to guessing the name from the local part and says so.
    // The caller distinguishes them, so nothing here turns a 404 into data.
    //
    // Cached per email, so going back to an address already tried costs
    // nothing.
    lookupBriefRequester: builder.query({
      query: ({ email, token }) => ({
        url: `api/briefs/requester-lookup/?email=${encodeURIComponent(email)}`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      transformResponse: (response) => response?.data ?? null,
    }),

    // TIP-611 — place an order. Answers 201 with the created brief.
    //
    // The body carries `idempotency_key`, minted when the form opened, so a
    // double press or a retry after a dropped response is the SAME order to
    // the backend and cannot spend two briefs. That is what makes every
    // failure here safe to retry, and why the modal offers a retry rather than
    // telling the customer to check whether it went through.
    createBriefOrder: builder.mutation({
      query: ({ payload, token }) => ({
        url: "api/briefs/orders/",
        method: "POST",
        body: payload,
        headers: {
          "content-type": "application/json",
          // Also as a header, which is where an idempotency key conventionally
          // lives, so a proxy or a future backend that looks there finds it.
          "idempotency-key": payload?.idempotency_key ?? "",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      transformResponse: (response) => response?.data ?? null,
      // A placed order spends a brief and adds a row, so both reads are stale
      // the moment this succeeds. Invalidated on failure too: a 409 means the
      // allowance already moved under us, and the card must catch up.
      invalidatesTags: (result, error, { payload }) => [
        { type: "BriefsAllowance", id: payload?.company_id },
        { type: "BriefsList", id: payload?.company_id },
      ],
    }),

    // TIP-613 — which company owns a bare reference.
    //
    //   GET /api/briefs/orders/<reference>/resolve/ -> { reference, company_id }
    //
    // The server-side half of the brief-ready email's deep link. The email
    // carries only ?ref=TRX-CB-00007, and the customer may act for several
    // companies — so before the brief can be opened the page has to know
    // WHOSE it is and switch the active company to match.
    //
    // Deliberately returns no file and no part of the masked download token:
    // it answers "whose is this, and may you see it" and nothing more. A 403
    // is a reference belonging to a company the user has no access to, a 404
    // one that does not exist; the page must not tell them apart beyond
    // "we cannot open this".
    resolveBriefReference: builder.query({
      query: ({ reference, token }) => ({
        url: `api/briefs/orders/${encodeURIComponent(reference)}/resolve/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      transformResponse: (response) => response?.data ?? null,
    }),

    // TIP-614 — buy extra briefs. Two endpoints, both under /api/billing/.
    //
    //   POST /api/billing/brief-checkout/            -> { client_secret }
    //   GET  /api/billing/brief-purchase/<session>/  -> { status, quantity, charged_total_fils }
    //
    // The body carries ONLY company_id and quantity. Quantity, tier and price
    // are never taken from the client: the backend validates 1-20 itself,
    // reads the tier from Company.is_member and the price from settings. The
    // quote this modal shows is a display artifact — Stripe's session totals
    // are the record. Sending a price would be ignored at best.
    createBriefCheckoutSession: builder.mutation({
      // `stayOnPage` asks the backend for redirect_on_completion='never', so
      // Stripe fires onComplete in the modal instead of navigating the whole
      // window away and remounting the dashboard.
      query: ({ companyID, quantity, token, stayOnPage = true }) => ({
        url: "api/billing/brief-checkout/",
        method: "POST",
        body: {
          company_id: companyID,
          quantity,
          ...(stayOnPage ? { stay_on_page: true } : {}),
        },
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      // Nothing is invalidated here: creating a session charges nothing and
      // credits nothing. The allowance only moves when the webhook lands, and
      // the poll below is what notices.
    }),

    // The post-payment poll. Stripe returns the customer to
    // ?purchase=<CHECKOUT_SESSION_ID> and the webhook credits the bucket
    // asynchronously, so this is asked until it stops saying "pending".
    //
    // A `pending` answer is 200, not 404, by design — a webhook that has not
    // landed yet must never read as a failed payment. So `pending` is a
    // SUCCESSFUL response carrying an unfinished status, and the caller polls
    // rather than retrying an error.
    getBriefPurchaseState: builder.query({
      query: ({ sessionId, token }) => ({
        url: `api/billing/brief-purchase/${encodeURIComponent(sessionId)}/`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      // Credited briefs change the allowance and nothing else. Invalidated
      // only once it is no longer pending, so a poll does not refetch the
      // card on every tick.
      invalidatesTags: (result, error, { companyID }) =>
        result && result.status && result.status !== "pending"
          ? [{ type: "BriefsAllowance", id: companyID }]
          : [],
    }),

    // Every paid brief purchase for a company, newest first — feeds the
    // Billing History table on the Billing page alongside membership/web-
    // profile Subscription rows.
    //
    //   GET /api/billing/brief-purchases/?company_id=<companyID>
    getBriefPurchases: builder.query({
      query: ({ companyID, token }) => ({
        url: `api/billing/brief-purchases/?company_id=${encodeURIComponent(companyID)}`,
        method: "GET",
        headers: {
          "content-type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
      transformResponse: (response) => response ?? [],
    }),

    // TIP-610 — the delivered PDF.
    //
    //   GET /api/briefs/orders/<reference>/download/
    //
    // A mutation, not a query: it is an action taken on a click, and its
    // result is a file the browser saves rather than state any component
    // renders. Caching it would hold whole PDFs in the store.
    //
    // The response is a Django FileResponse — the BYTES, with the filename in
    // Content-Disposition — so `responseHandler` reads a blob on success and
    // falls back to JSON on a failure, which is where the server's own
    // message lives. Without that branch a 403's envelope would arrive as an
    // unreadable blob and every failure would look the same.
    //
    // The status is checked again server-side: the analyst's upload makes the
    // file exist before the customer is told it is ready, so readiness is the
    // mechanism that keeps it private, not just a disabled button.
    downloadBriefOrder: builder.mutation({
      query: ({ reference, token }) => ({
        url: `api/briefs/orders/${encodeURIComponent(reference)}/download/`,
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseHandler: (response) =>
          response.ok ? response.blob() : response.json().catch(() => null),
      }),
      // `meta.response` is the only place the filename exists.
      transformResponse: (blob, meta) => ({
        blob,
        disposition: meta?.response?.headers?.get("content-disposition") || "",
      }),
    }),
  }),
});

export const {
  useGetBriefsAllowanceQuery,
  useGetBriefsListQuery,
  // The order form asks this on blur of the subject name, to warn about a
  // brief the customer already has for the same company. Lazy: it fires when
  // the field is left, not while the modal renders.
  useLazyGetBriefsListQuery,
  useGetBriefDetailQuery,
  useCreateBriefOrderMutation,
  useLazyLookupBriefRequesterQuery,
  useDownloadBriefOrderMutation,
  useCreateBriefCheckoutSessionMutation,
  useLazyGetBriefPurchaseStateQuery,
  useLazyResolveBriefReferenceQuery,
  useGetBriefPurchasesQuery,
} = briefsApiSlice;
